import moment from "moment";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useIntercom } from "react-intercom-hook";
import { useHistory, useLocation } from "react-router-dom";
import useSWR from "swr";
import { AuthenticationState, User } from ".";
import { ApiResponse, fetchApi } from "../../utils/api";
import { GenericFunction, Provider } from "../../utils/types";
import { useGoogleAnalytics } from "../googleAnalytics";
import { useToast } from "../toast";
import * as dataMethods from "./data-methods";

interface ContextValue {
  user: User;
  wrapActionWithAuth: <TArgs, TRet>(
    fn: GenericFunction<TArgs, TRet>
  ) => GenericFunction<TArgs, TRet>;
  authenticationState: AuthenticationState;
  startLoginProcess: (
    provider: Provider,
    returnToUrl?: string
  ) => Promise<void>;
  checkIsUserLoggedIn: (verifyAgain?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  addEmail: (email: string) => Promise<ApiResponse<any>>;
  openChat: () => void;
}

const UserContext = React.createContext<ContextValue | undefined>(undefined);

const LOGGED_OUT_USER: User = {
  avatar_url: "",
  email: "",
  locale: "",
  name: "Guest",
  provider_identifier: "",
  provider_nickname: "",
  provider_type: "",
  uuid: "",
  created_at: "",
  updated_at: "",
  affiliate_code_used: null,
  workspaces: []
};

const setReturnToUrl = (returnToUrl: string) => {
  localStorage.setItem(`return-to-url`, returnToUrl);
};

export const consumeReturnUrl = (): string | null => {
  const url = localStorage.getItem("return-to-url");
  localStorage.removeItem("return-to-url");

  return url;
};

export const generateReturnToUrl = (location: any): string => {
  const searchParams = new URLSearchParams(location.search);
  searchParams.set("returnToUrl", location.pathname);

  return searchParams.toString();
};

const AuthenticationProvider = ({ children }: PropsWithChildren<{}>) => {
  const [user, setUser] = useState<User>(
    localStorage.getItem("user")
      ? (JSON.parse(localStorage.getItem("user") as string) as User)
      : { ...LOGGED_OUT_USER }
  );
  const { trackEvent } = useGoogleAnalytics();
  const [authenticationState, setAuthenticationState] =
    useState<AuthenticationState>("checking");
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();
  const trackedSignup = useRef(false);
  const checkingLoginRef = useRef<Promise<User | null>>();
  const { boot, show } = useIntercom();

  const setGoaffReferal = useCallback((user: User) => {
    var goaffproOrder = {
      number: user.uuid,
      total: 0,
      date: new Date(),
      customer: {
        first_name: user.name,
        email: user.email
      }
    };

    const globalWindow: Window | any = window;

    if (!globalWindow?.goaffproTrackConversion) return;

    globalWindow.goaffproTrackConversion(goaffproOrder);
  }, []);

  const checkIsUserLoggedIn = useCallback(
    async (verifyAgain?: boolean) => {
      if (authenticationState === "authenticated" && !verifyAgain) return;

      if (checkingLoginRef.current && !verifyAgain) return;

      checkingLoginRef.current = dataMethods.isUserLoggedIn();
      const user = await checkingLoginRef.current;

      if (user) {
        setUser(user);
        setAuthenticationState("authenticated");
        localStorage.setItem("user", JSON.stringify(user));
      } else {
        setAuthenticationState("unauthenticated");
      }
    },
    [authenticationState]
  );

  useSWR("is-logged-in", checkIsUserLoggedIn, {
    refreshInterval: 60 * 1000,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshWhenHidden: false
  });

  useEffect(() => {
    if (
      !trackedSignup.current &&
      user?.created_at &&
      moment().utc().diff(moment(user.created_at).utc(), "seconds") < 5
    ) {
      trackedSignup.current = true;

      if (!user.affiliate_code_used) {
        setGoaffReferal(user);
      }
      trackEvent("signup", { method: "Discord" });
    }
  }, [setGoaffReferal, trackEvent, user]);

  const wrapActionWithAuth: ContextValue["wrapActionWithAuth"] = useCallback(
    (fn) => {
      return (...args) => {
        if (authenticationState === "authenticated") return fn(...args);

        addToast({
          type: "info",
          waitForInteraction: true,
          message: (
            <div>
              <p>Please log in to use this feature.</p>
              <button
                type="button"
                className="mt-2 inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
                onClick={() => {
                  history.push(
                    `/auth/discord?${generateReturnToUrl(location)}`
                  );
                }}
              >
                Login
              </button>
            </div>
          )
        });
      };
    },
    [addToast, authenticationState, history, location]
  );

  const startLoginProcess = useCallback(
    async (provider: Provider, returnToUrl?: string) => {
      if (authenticationState === "authenticated") return;

      if (returnToUrl) {
        setReturnToUrl(returnToUrl);
      }

      trackEvent("login", {
        method: provider
      });

      try {
        const loginUrl = await dataMethods.getLoginUrl(
          generateReturnToUrl(location),
          provider
        );
        window.location.assign(loginUrl);
      } catch (err) {
        console.error(err);

        history.replace(`/auth/${provider}/cb?error=invalid_request`);
      }
    },
    [authenticationState, history, location, trackEvent]
  );

  const addEmail = useCallback(
    async (email: string) => {
      try {
        const response = await fetchApi(`/api/personal-data/email`, {
          method: "PUT",
          body: JSON.stringify({ email })
        });

        const apiResponse: ApiResponse<User> = await response.json();

        checkIsUserLoggedIn();

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [checkIsUserLoggedIn]
  );

  const logout = useCallback(async () => {
    await dataMethods.logOut();

    setAuthenticationState("unauthenticated");
    setUser({ ...LOGGED_OUT_USER });
    localStorage.removeItem("user");
    history.replace(`/`);
  }, [history]);

  const openChat = useCallback(() => {
    const intercomConfig: {
      hide_default_launcher: boolean;
      name?: string;
      email?: string;
    } = {
      hide_default_launcher: true
    };

    if (authenticationState === "authenticated" && user?.name) {
      intercomConfig.name = user.name;
      intercomConfig.email = user.email;
    }

    boot(intercomConfig);

    show();
  }, [authenticationState, boot, show, user.email, user.name]);

  const value = useMemo(
    () => ({
      authenticationState,
      checkIsUserLoggedIn,
      logout,
      startLoginProcess,
      user,
      wrapActionWithAuth,
      addEmail,
      openChat
    }),
    [
      authenticationState,
      checkIsUserLoggedIn,
      logout,
      startLoginProcess,
      user,
      wrapActionWithAuth,
      addEmail,
      openChat
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useAuthentication = (): ContextValue => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
};

export { useAuthentication, AuthenticationProvider };
